import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { groupService } from "@/services";
import type { GroupTicketView, GroupView } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { Badge } from "@/components/base/Badge";
import { Avatar } from "@/components/base/Avatar";
import { Tooltip } from "@/components/base/Tooltip";
import { ErrorState } from "@/components/base/ErrorState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";
import { canActivate, emptyTicketCount } from "@/utils/group";
import { AssignModal } from "./components/AssignModal";
import { BulkAssignModal } from "./components/BulkAssignModal";
import { ActivateModal } from "../detail/components/ActivateModal";
import { ReplaceMemberModal } from "../detail/components/ReplaceMemberModal";
import { ChangeTicketModal } from "../detail/components/ChangeTicketModal";
import { AddMemberModal } from "./components/AddMemberModal";

export default function GroupEnrollmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isViewer } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();

  const [group, setGroup] = useState<GroupView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assignSeat, setAssignSeat] = useState<number | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [replacingTicket, setReplacingTicket] = useState<GroupTicketView | null>(null);
  const [editingTicket, setEditingTicket] = useState<GroupTicketView | null>(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      setGroup(await groupService.getGroupView(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load group.");
      setGroup(null);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div>
        <PageHeader breadcrumb={<BreadcrumbItem label="Groups" to="/groups" />} title="Enrolment" />
        <Card><ErrorState message={error} onRetry={load} /></Card>
      </div>
    );
  }

  if (!group) {
    return (
      <div>
        <PageHeader breadcrumb={<BreadcrumbItem label="Groups" to="/groups" />} title="Loading enrolment…" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-lg bg-background-200" />
          ))}
        </div>
      </div>
    );
  }

  const editable = group.status === "draft" && !isViewer;
  const canReplace = group.status === "active" && !isViewer;
  const canEditTicket = (group.status === "draft" || group.status === "active") && !isViewer;
  const filled = group.tickets.filter((t) => t.member).length;
  const empty = emptyTicketCount(group);
  const ready = canActivate(group);
  const missing = [
    !(group.tickets.length >= group.memberCount) ? "every ticket must be filled" : "",
    !group.tickets.some((t) => t.memberId === group.organizerMemberId) ? "the organizer must hold a ticket" : "",
  ].filter(Boolean);

  const removeTicket = async (ticket: GroupTicketView) => {
    const ok = await confirm({
      title: "Remove member from ticket?",
      message: `${ticket.member?.fullName ?? "This member"} will be removed from ticket #${ticket.seat}.`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await groupService.removeTicket(group.id, ticket.memberId);
      toast.success("Ticket freed", `Ticket #${ticket.seat} is now empty.`);
      await load();
    } catch (err) {
      toast.error("Could not remove member", err instanceof Error ? err.message : "Please try again.");
    }
  };

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Groups" to="/groups" />
            <BreadcrumbItem label={group.name} to={`/groups/${group.id}`} />
            <BreadcrumbItem label="Enrolment" isLast />
          </>
        }
        title={
          <span className="flex items-center gap-2">
            <i className="ri-user-settings-line text-primary-600 text-lg" />
            Enrolment & tickets
          </span>
        }
        subtitle={`${group.name} (${group.groupCode})`}
        actions={
          !isViewer ? (
            <div className="flex flex-wrap items-center gap-2">
              {group.status === "draft" || group.status === "active" ? (
                group.completedCycles > 0 ? (
                  <Tooltip side="bottom" content="Cannot add member: changes are only possible if no cycle/auction has been done yet (Policy 2).">
                    <span>
                      <Button variant="outline" disabled icon={<i className="ri-user-add-line" />}>
                        Add member
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button variant="outline" icon={<i className="ri-user-add-line" />} onClick={() => setAddMemberOpen(true)}>
                    Add member
                  </Button>
                )
              ) : null}

              {editable ? (
                <>
                  <Button variant="outline" icon={<i className="ri-add-line" />} disabled={empty === 0} onClick={() => setBulkOpen(true)}>
                    Bulk assign
                  </Button>
                  {ready ? (
                    <Button icon={<i className="ri-rocket-line" />} onClick={() => setActivateOpen(true)}>
                      Activate group
                    </Button>
                  ) : (
                    <Tooltip side="bottom" content={`Activation needs: ${missing.join(" and ")}.`}>
                      <Button disabled icon={<i className="ri-rocket-line" />}>
                        Activate group
                      </Button>
                    </Tooltip>
                  )}
                </>
              ) : null}
            </div>
          ) : null
        }
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <p className="text-sm font-semibold text-foreground-900">
              <span className="tnum">{filled}</span> of <span className="tnum">{group.memberCount}</span> tickets filled
            </p>
            {group.status === "draft" ? <Badge variant="pending">Draft</Badge> : <Badge variant={group.status === "completed" ? "completed" : "active"} dot>{group.status === "completed" ? "Completed" : "Active"}</Badge>}
          </div>
          <div className="h-2 w-48 overflow-hidden rounded-full bg-background-200">
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${(filled / group.memberCount) * 100}%` }} />
          </div>
        </div>
        {group.status !== "draft" ? (
          <p className="mt-3 text-xs text-foreground-500">
            This group is active — new ticket creation is locked. To replace an existing non-winning member, click the <i className="ri-user-shared-line mx-0.5 text-primary-600" /> replace icon on their ticket.
          </p>
        ) : (
          <p className="mt-3 text-xs text-foreground-500">
            Assign a member to each numbered ticket. The organizer's ticket is marked — they must hold one before the group can activate.
          </p>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {group.tickets.map((t) =>
          t.member ? (
            <div
              key={t.seat}
              className={
                t.isOrganizer
                  ? "rounded-lg border border-accent-300 bg-accent-50/50 p-3 shadow-xs"
                  : "rounded-lg border border-background-200 bg-background-50 p-3 shadow-xs"
              }
            >
              <div className="flex items-start justify-between gap-2">
                <span className="tnum text-xs font-semibold text-primary-700 flex items-center gap-1.5">
                  <i className="ri-ticket-2-line text-xs" /> Ticket #{t.seat}
                </span>
                <div className="flex items-center gap-1">
                  {t.isOrganizer ? <Badge color="accent" dot>Organizer</Badge> : null}
                  {t.wonCycle != null ? <Badge color="neutral" size="sm">Won #{t.wonCycle}</Badge> : null}

                  {canEditTicket && t.groupMemberId ? (
                    <Tooltip side="top" content="Change ticket number">
                      <button
                        type="button"
                        onClick={() => setEditingTicket(t)}
                        title="Change ticket number"
                        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      >
                        <i className="ri-pencil-line text-sm" />
                      </button>
                    </Tooltip>
                  ) : null}

                  {editable && !t.isOrganizer ? (
                    <button
                      type="button"
                      onClick={() => removeTicket(t)}
                      title="Remove"
                      className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-danger-50 hover:text-danger-600 transition-colors"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  ) : null}

                  {canReplace ? (
                    t.isOrganizer ? (
                      <Tooltip side="left" content="Organizer cannot leave or be replaced (Rule 1).">
                        <span className="flex h-6 w-6 items-center justify-center rounded text-foreground-300 opacity-50 cursor-not-allowed">
                          <i className="ri-user-shared-line text-sm" />
                        </span>
                      </Tooltip>
                    ) : t.wonCycle != null ? (
                      <Tooltip side="left" content={`Member won in Cycle ${t.wonCycle}. Prize winners cannot be replaced (Rule 2 & 3).`}>
                        <span className="flex h-6 w-6 items-center justify-center rounded text-foreground-300 opacity-50 cursor-not-allowed">
                          <i className="ri-user-shared-line text-sm" />
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip side="left" content="Replace member">
                        <button
                          type="button"
                          onClick={() => setReplacingTicket(t)}
                          title="Replace member"
                          className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                        >
                          <i className="ri-user-shared-line text-sm" />
                        </button>
                      </Tooltip>
                    )
                  ) : null}
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2.5">
                <Avatar name={t.member.fullName} size="sm" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground-900">{t.member.fullName}</p>
                  <p className="tnum truncate text-xs text-foreground-500">
                    {t.member.memberCode} · {t.member.primaryPhone}
                  </p>
                </div>
              </div>

              {t.otherGroups.length > 0 ? (
                <Tooltip
                  side="bottom"
                  content={
                    <span>
                      Also enrolled in {t.otherGroups.length} other group{t.otherGroups.length === 1 ? "" : "s"}:
                      {t.otherGroups.map((g) => (
                        <span key={g.groupId} className="block py-0.5">
                          {g.groupName} · outstanding <CurrencyDisplay amount={g.outstanding} hideSymbol className="text-success-300" />
                        </span>
                      ))}
                    </span>
                  }
                >
                  <p className="mt-2 inline-flex cursor-help items-center gap-1 text-xs text-secondary-700">
                    <i className="ri-group-line" /> {t.otherGroups.length} other group{t.otherGroups.length === 1 ? "" : "s"}
                  </p>
                </Tooltip>
              ) : null}
            </div>
          ) : (
            <div key={t.seat} className="flex min-h-[7.5rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100/40 p-3">
              <span className="tnum text-xs font-semibold text-foreground-400 flex items-center gap-1.5">
                <i className="ri-ticket-2-line text-xs" /> Ticket #{t.seat}
              </span>
              <span className="text-xs text-foreground-400">Empty</span>
              {editable ? (
                <Button size="sm" variant="outline" icon={<i className="ri-user-add-line" />} onClick={() => setAssignSeat(t.seat)}>
                  Assign member
                </Button>
              ) : null}
            </div>
          )
        )}
      </div>

      {/* Replaced Members / Accounts on Hold Section */}
      {group.replacedMembers && group.replacedMembers.length > 0 ? (
        <div className="mt-6 rounded-xl border border-background-200 bg-background-50/70 p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 pb-3">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded bg-amber-100 text-amber-700">
                <i className="ri-history-line text-sm" />
              </span>
              <h3 className="text-sm font-semibold text-foreground-900">
                Replaced Members (Accounts on Hold)
              </h3>
              <Badge color="warning" size="sm">{group.replacedMembers.length}</Badge>
            </div>
            <span className="text-xs text-foreground-500">
              Hold Mode: Excluded from active demands & auctions
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-foreground-700">
              <thead className="border-b border-background-200 text-[11px] uppercase tracking-wider text-foreground-400">
                <tr>
                  <th className="py-2 px-3">Ticket</th>
                  <th className="py-2 px-3">Exited Member</th>
                  <th className="py-2 px-3">Phone</th>
                  <th className="py-2 px-3">Hold Status</th>
                  <th className="py-2 px-3">Exited On</th>
                  <th className="py-2 px-3">Remarks / Replacement Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/80">
                {group.replacedMembers.map((row) => (
                  <tr key={row.groupMemberId} className="hover:bg-background-100/50 transition-colors">
                    <td className="py-2.5 px-3 font-semibold text-primary-700">#{row.ticketNo}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <Avatar name={row.fullName} size="xs" />
                        <div>
                          <div className="font-medium text-foreground-900">{row.fullName}</div>
                          <div className="text-[11px] text-foreground-400">{row.memberCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 tnum">{row.phone}</td>
                    <td className="py-2.5 px-3">
                      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        <i className="ri-pause-circle-line" /> REPLACED (HOLD)
                      </span>
                    </td>
                    <td className="py-2.5 px-3 tnum">{row.exitedOn ? formatDate(row.exitedOn) : "—"}</td>
                    <td className="py-2.5 px-3 text-foreground-600 max-w-xs truncate" title={row.remarks ?? ""}>
                      {row.remarks || "Replaced by incoming member"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <AssignModal
        open={assignSeat != null}
        group={group}
        seat={assignSeat ?? 1}
        onClose={() => setAssignSeat(null)}
        onAssigned={() => {
          setAssignSeat(null);
          load();
        }}
      />
      <BulkAssignModal
        open={bulkOpen}
        group={group}
        onClose={() => setBulkOpen(false)}
        onAssigned={() => {
          setBulkOpen(false);
          load();
        }}
      />
      <ActivateModal
        open={activateOpen}
        group={group}
        onClose={() => setActivateOpen(false)}
        onActivated={(gid) => {
          navigate(`/groups/${gid}`);
        }}
      />
      <ReplaceMemberModal
        open={Boolean(replacingTicket)}
        group={group}
        ticket={replacingTicket}
        onClose={() => setReplacingTicket(null)}
        onSuccess={() => {
          setReplacingTicket(null);
          load();
        }}
      />
      <ChangeTicketModal
        open={Boolean(editingTicket)}
        group={group}
        ticket={editingTicket}
        onClose={() => setEditingTicket(null)}
        onSuccess={() => {
          setEditingTicket(null);
          load();
        }}
      />
      <AddMemberModal
        open={addMemberOpen}
        group={group}
        onClose={() => setAddMemberOpen(false)}
        onSuccess={() => {
          setAddMemberOpen(false);
          load();
        }}
      />
    </div>
  );
}